/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/best-sellers.tsx — its
 * `WidgetCard` and thumbnail-title rows, fed by `GET /api/identity/invitations` (the whole
 * outstanding set, unpaged) instead of `topProducts`. The count is the API's own `totalCount`; the
 * rows are the first five in the order received.
 */
import { Link } from "react-router-dom";
import { WidgetCard } from "@steelyard/theme";
import { Badge, Text } from "rizzui";
import { PiEnvelopeSimpleDuotone as Envelope } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile, countOf, UnreadableWidgetCard } from "./TrendStatCard";
import { formatDate, roleName } from "../../lib/format";
import type { InvitationList } from "../../lib/services/invitations";

const TITLE = "Invited, not yet joined";

export function PendingInvitationsTile({ resolve }: { resolve: Promise<InvitationList> }) {
  const unreadable = (error: unknown) => (
    <UnreadableWidgetCard title={TITLE} error={error} fallback="Could not be read — this is not the same as no open invitations." />
  );

  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[380px]">
      {({ items, totalCount }) => (
        <WidgetCard
          title={
            <span className="flex items-center gap-2">
              {TITLE}
              <Badge variant="flat" color={totalCount === 0 ? "success" : "warning"} rounded="pill" size="sm">
                {totalCount}
              </Badge>
            </span>
          }
          description={`${countOf(totalCount, "invitation")} not yet accepted`}
          descriptionClassName="mt-1 text-gray-500"
          className="flex flex-col"
          action={
            <Link to="/invitations" className="whitespace-nowrap text-sm font-medium underline">
              View all
            </Link>
          }
        >
          {items.length === 0 ? (
            <EmptyTile icon={<Envelope />} title="No open invitations.">
              Everyone invited has accepted, or the invitation was revoked or expired.
            </EmptyTile>
          ) : (
            <ul className="mt-[18px] grid grid-cols-1 gap-4 @sm:gap-5">
              {items.slice(0, 5).map((invitation) => (
                <li key={invitation.id} className="flex min-w-0 items-center pe-2">
                  <div className="me-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary @sm:h-12 @sm:w-12">
                    <Envelope aria-hidden className="size-5" />
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Text className="truncate font-lexend text-sm font-medium text-gray-900 dark:text-gray-700">{invitation.email ?? "—"}</Text>
                      <Text className="truncate text-gray-500">
                        {[roleName(invitation.role), invitation.companyName].filter(Boolean).join(" · ")}
                      </Text>
                    </div>
                    {/* The API still lists an invitation past its expiry; a lapsed link can only be resent. */}
                    {Date.parse(invitation.expiresAt) < Date.now() ? (
                      <Badge variant="flat" color="danger" rounded="pill" size="sm" className="shrink-0">
                        Expired {formatDate(invitation.expiresAt)}
                      </Badge>
                    ) : (
                      <Text className="shrink-0 text-sm text-gray-500">Expires {formatDate(invitation.expiresAt)}</Text>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </WidgetCard>
      )}
    </TileBoundary>
  );
}
